import { useQueryClient } from "@tanstack/react-query";
import { addDays, addHours } from "date-fns";
import { type FormEvent, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { APIError, api, jsonBody } from "../../api/client";
import type { Booking, BookingConflict } from "../../api/types";
import { formatDateTime, localInputValue } from "../../lib/format";
import { Card, EditorPage, ErrorPanel, Field, FormActions, Notice } from "../../ui/components";
import { ContactPicker } from "../contacts/ContactPicker";
import { useAllLocations, useVenues } from "../venues/queries";
import { buildRecurrence, type RecurrencePattern } from "./recurrence";

interface ConflictResult {
  conflicts: BookingConflict[];
  canOverride: boolean;
}

export function BookingCreatePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const venuesQuery = useVenues();
  const venues = venuesQuery.data?.items ?? [];
  const locationsQuery = useAllLocations(venues);
  const requestedDate = searchParams.get("date");
  const requestedStart = requestedDate ? new Date(`${requestedDate}T09:00:00`) : undefined;
  const validRequestedStart =
    requestedStart && !Number.isNaN(requestedStart.getTime()) ? requestedStart : undefined;
  const startDefault = validRequestedStart ?? addDays(new Date(), 1);
  if (!validRequestedStart) startDefault.setMinutes(0, 0, 0);
  const [error, setError] = useState<unknown>();
  const [submitting, setSubmitting] = useState(false);
  const [conflicts, setConflicts] = useState<ConflictResult | null>(null);
  const [pendingPayload, setPendingPayload] = useState<Record<string, unknown> | null>(null);
  const [overrideConfirmed, setOverrideConfirmed] = useState(false);
  const [overrideReason, setOverrideReason] = useState("");
  const [repeatPattern, setRepeatPattern] = useState<RecurrencePattern>("none");
  const [repeatCount, setRepeatCount] = useState(4);

  function leave() {
    navigate("/calendar");
  }

  async function create(payload: Record<string, unknown>) {
    setSubmitting(true);
    setError(undefined);
    try {
      const created = await api<Booking>("/api/v1/bookings", {
        method: "POST",
        body: jsonBody(payload),
      });
      await queryClient.invalidateQueries({ queryKey: ["bookings"] });
      navigate(`/bookings/${created.id}`, { replace: true });
    } catch (caught) {
      if (caught instanceof APIError && caught.code === "booking_conflict") {
        setConflicts({
          conflicts: (caught.details.conflicts as BookingConflict[]) ?? [],
          canOverride: Boolean(caught.details.canOverride),
        });
        setPendingPayload(payload);
      } else {
        setError(caught);
      }
    } finally {
      setSubmitting(false);
    }
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSubmitting(true);
    setError(undefined);
    try {
      const data = new FormData(event.currentTarget);
      const locationId = String(data.get("locationId") ?? "");
      const location = locationsQuery.locations.find((item) => item.id === locationId);
      const venue = venues.find((item) => item.id === location?.venueId);
      if (!location || !venue) throw new Error("Select an available venue location.");
      const timezone = location.timezone || venue.timezone;
      const attendeeRaw = String(data.get("attendeeCount") ?? "");
      const setupMinutes = Number(data.get("setupMinutes") ?? 0);
      const teardownMinutes = Number(data.get("teardownMinutes") ?? 0);
      const series = buildRecurrence({
        startLocal: String(data.get("start") ?? ""),
        endLocal: String(data.get("end") ?? ""),
        timezone,
        pattern: repeatPattern,
        count: repeatCount,
        setupMinutes,
        teardownMinutes,
      });
      const payload: Record<string, unknown> = {
        customerId: String(data.get("customerId") ?? ""),
        title: String(data.get("title") ?? ""),
        description: String(data.get("description") ?? ""),
        timezone,
        attendeeCount: attendeeRaw ? Number(attendeeRaw) : null,
        internalNotes: String(data.get("internalNotes") ?? ""),
        customerNotes: String(data.get("customerNotes") ?? ""),
        status: String(data.get("status") ?? "provisional"),
        locationIds: [locationId],
        occurrences: series.occurrences,
        recurrenceRule: series.recurrenceRule,
        confirmConflictOverride: false,
        conflictOverrideReason: "",
      };
      const result = await api<ConflictResult>("/api/v1/bookings/check-conflicts", {
        method: "POST",
        body: jsonBody(payload),
      });
      if (result.conflicts.length) {
        setConflicts(result);
        setPendingPayload(payload);
      } else {
        await create(payload);
      }
    } catch (caught) {
      setError(caught);
    } finally {
      setSubmitting(false);
    }
  }

  async function override() {
    if (!pendingPayload || !overrideConfirmed || overrideReason.trim().length < 10) return;
    await create({
      ...pendingPayload,
      confirmConflictOverride: true,
      conflictOverrideReason: overrideReason,
    });
  }

  return (
    <EditorPage
      title="Create booking"
      description="Schedule a venue location, recurrence, notes, and conflict handling in one focused workflow."
      backTo="/calendar"
      backLabel="Back to calendar"
    >
      {error ? <ErrorPanel error={error} /> : null}
      {conflicts ? (
        <Card>
          <div className="stack">
            <Notice tone="danger" title="Schedule conflict detected">
              This time overlaps an existing allocation. Review every conflict before deciding what
              to do.
            </Notice>
            {conflicts.conflicts.map((conflict) => (
              <div className="card" key={conflict.allocationId}>
                <strong>
                  {conflict.bookingReference} · {conflict.bookingTitle}
                </strong>
                <p>
                  {conflict.locationName}
                  <br />
                  {formatDateTime(conflict.start)}–{formatDateTime(conflict.end)}
                </p>
              </div>
            ))}
            {conflicts.canOverride ? (
              <>
                <label className="check-field">
                  <input
                    type="checkbox"
                    checked={overrideConfirmed}
                    onChange={(event) => setOverrideConfirmed(event.target.checked)}
                  />
                  <span>
                    I understand this creates an intentional double-booking and the decision will be
                    audited.
                  </span>
                </label>
                <Field
                  label="Required override reason"
                  hint="At least 10 characters. Be specific enough for a later audit."
                >
                  <textarea
                    value={overrideReason}
                    onChange={(event) => setOverrideReason(event.target.value)}
                  />
                </Field>
                <div className="form-actions">
                  <button
                    className="button secondary"
                    type="button"
                    onClick={() => {
                      setConflicts(null);
                      setPendingPayload(null);
                    }}
                  >
                    Change the schedule
                  </button>
                  <button
                    className="button danger"
                    type="button"
                    disabled={submitting || !overrideConfirmed || overrideReason.trim().length < 10}
                    onClick={override}
                  >
                    Confirm override
                  </button>
                </div>
              </>
            ) : (
              <>
                <Notice tone="warning">
                  Your role cannot override this conflict. Change the schedule or ask an authorized
                  colleague.
                </Notice>
                <button
                  className="button secondary"
                  type="button"
                  onClick={() => {
                    setConflicts(null);
                    setPendingPayload(null);
                  }}
                >
                  Change the schedule
                </button>
              </>
            )}
          </div>
        </Card>
      ) : (
        <Card>
          <form className="form-stack" onSubmit={submit}>
            <div className="form-grid">
              <Field
                label="Customer"
                hint="Search by name, organization or email. Create them here if they are new."
              >
                <ContactPicker name="customerId" required />
              </Field>
              <Field label="Location">
                <select name="locationId" required defaultValue="">
                  <option value="" disabled>
                    {locationsQuery.loading ? "Loading locations…" : "Select a location"}
                  </option>
                  {venues.map((venue) => (
                    <optgroup key={venue.id} label={venue.name}>
                      {locationsQuery.locations
                        .filter(
                          (location) =>
                            location.venueId === venue.id && location.isBookable !== false,
                        )
                        .map((location) => (
                          <option key={location.id} value={location.id}>
                            {location.name}
                          </option>
                        ))}
                    </optgroup>
                  ))}
                </select>
              </Field>
              <Field label="Booking title">
                <input name="title" required maxLength={240} />
              </Field>
              <Field label="Initial status">
                <select name="status" defaultValue="provisional">
                  <option value="draft">Draft</option>
                  <option value="enquiry">Inquiry</option>
                  <option value="held">Held</option>
                  <option value="provisional">Provisional</option>
                  <option value="pending_approval">Pending approval</option>
                  <option value="confirmed">Confirmed</option>
                </select>
              </Field>
              <Field label="Starts">
                <input
                  name="start"
                  type="datetime-local"
                  required
                  defaultValue={localInputValue(startDefault)}
                />
              </Field>
              <Field label="Ends">
                <input
                  name="end"
                  type="datetime-local"
                  required
                  defaultValue={localInputValue(addHours(startDefault, 2))}
                />
              </Field>
              <Field
                label="Repeats"
                hint="Every occurrence is checked for conflicts before the series is created."
              >
                <select
                  value={repeatPattern}
                  onChange={(event) => setRepeatPattern(event.target.value as RecurrencePattern)}
                >
                  <option value="none">Does not repeat</option>
                  <option value="weekly">Weekly</option>
                  <option value="biweekly">Every two weeks</option>
                  <option value="monthly">Monthly</option>
                </select>
              </Field>
              {repeatPattern !== "none" ? (
                <Field
                  label="Number of occurrences"
                  hint="Between 2 and 52, including the first date."
                >
                  <input
                    type="number"
                    min={2}
                    max={52}
                    value={repeatCount}
                    onChange={(event) => setRepeatCount(Number(event.target.value))}
                    required
                  />
                </Field>
              ) : null}
              <Field label="Setup minutes">
                <input name="setupMinutes" type="number" min={0} max={1440} defaultValue={0} />
              </Field>
              <Field label="Teardown minutes">
                <input name="teardownMinutes" type="number" min={0} max={1440} defaultValue={0} />
              </Field>
              <Field label="Attendees">
                <input name="attendeeCount" type="number" min={0} />
              </Field>
              <Field label="Description">
                <textarea name="description" maxLength={10000} />
              </Field>
              <Field label="Customer-facing notes">
                <textarea name="customerNotes" maxLength={10000} />
              </Field>
              <Field label="Internal notes">
                <textarea name="internalNotes" maxLength={20000} />
              </Field>
            </div>
            {venuesQuery.error || locationsQuery.error ? (
              <ErrorPanel error={venuesQuery.error ?? locationsQuery.error} />
            ) : null}
            <FormActions submitting={submitting} submitLabel="Check and create" onCancel={leave} />
          </form>
        </Card>
      )}
    </EditorPage>
  );
}
